import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PACKAGES } from "@/lib/data";

const FILTERS = [
  { id: "all", label: "All Homes" },
  { id: "starter", label: "Front Roof Line" },
  { id: "estate", label: "Estate" },
  { id: "signature", label: "Signature" },
];

const seen = new Set();
const ITEMS = PACKAGES.flatMap((p) =>
  p.gallery.map((src, i) => ({ src, pkg: p.id, alt: `${p.name} — look ${i + 1}`, name: p.name }))
).filter((it) => (seen.has(it.src) ? false : (seen.add(it.src), true)));

export const Gallery = () => {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? ITEMS : ITEMS.filter((it) => it.pkg === filter);

  return (
    <section id="gallery" className="relative border-y border-amber-500/10 bg-[#08170F] py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="mb-4 font-mono-x text-xs uppercase tracking-[0.28em] text-amber-400">Mockup Gallery</p>
            <h2 className="font-display max-w-xl text-3xl font-bold tracking-tight text-amber-50 sm:text-5xl">
              See what your house <span className="italic text-amber-400">could look like.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`rounded-full border px-4 py-2 font-mono-x text-[11px] uppercase tracking-widest transition-all ${
                  filter === f.id
                    ? "border-amber-400 bg-amber-500/15 text-amber-300"
                    : "border-emerald-500/25 text-emerald-100/60 hover:border-amber-500/40 hover:text-amber-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((it, i) => (
              <motion.figure
                layout
                key={`${it.pkg}-${it.src}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                className={`group relative overflow-hidden rounded-2xl border border-amber-500/15 ${i % 5 === 0 ? "sm:row-span-2" : ""}`}
              >
                <img
                  src={it.src}
                  alt={it.alt}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 5 === 0 ? "aspect-[3/4] sm:h-full" : "aspect-[4/3]"}`}
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#06130D]/95 to-transparent p-4 pt-10">
                  <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-amber-300">{it.name}</span>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
